import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import Menu from '@mui/material/Menu';
import Typography from '@mui/material/Typography';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppNotification } from '../../api/notifications';
import useNotifications, { resolveNotificationLink } from '../../hooks/useNotifications';

const formatTime = (iso: string) => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

function NotificationItem({
  notification,
  onSelect,
}: {
  notification: AppNotification;
  onSelect: (notification: AppNotification) => void;
}) {
  const isUnread = !notification.isRead;

  return (
    <ListItemButton
      onClick={() => onSelect(notification)}
      sx={{
        alignItems: 'flex-start',
        px: 2,
        py: 1.5,
        gap: 1.5,
        borderLeft: 3,
        borderLeftStyle: 'solid',
        borderLeftColor: isUnread ? 'primary.main' : 'transparent',
        bgcolor: isUnread ? 'rgba(0, 132, 109, 0.06)' : 'background.paper',
        '&:hover': {
          bgcolor: isUnread ? 'rgba(0, 132, 109, 0.1)' : 'action.hover',
        },
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
          <Typography
            variant="body2"
            fontWeight={isUnread ? 700 : 500}
            color={isUnread ? 'text.primary' : 'text.secondary'}
            sx={{ lineHeight: 1.4 }}
          >
            {notification.title}
          </Typography>
          {isUnread && (
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: 'primary.main',
                flexShrink: 0,
                mt: 0.5,
              }}
            />
          )}
        </Box>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            whiteSpace: 'pre-line',
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            fontSize: '0.8125rem',
            lineHeight: 1.45,
          }}
        >
          {notification.message}
        </Typography>
        <Typography variant="caption" color="text.disabled" sx={{ mt: 0.75, display: 'block' }}>
          {formatTime(notification.createdAt)}
        </Typography>
      </Box>
    </ListItemButton>
  );
}

export default function NotificationMenu() {
  const navigate = useNavigate();
  const { notifications, unreadCount, loading, markAsRead, refresh } = useNotifications();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const sortedNotifications = useMemo(
    () =>
      [...notifications].sort((a, b) => {
        if (a.isRead !== b.isRead) return a.isRead ? 1 : -1;
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      }),
    [notifications],
  );

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    refresh();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = async (notification: AppNotification) => {
    if (!notification.isRead) {
      await markAsRead(notification.id);
    }
    handleClose();
    const link = resolveNotificationLink(notification.linkUrl);
    if (link) {
      navigate(link);
    }
  };

  return (
    <>
      <IconButton onClick={handleOpen} aria-label="Notifications" size="small">
        <Badge badgeContent={unreadCount} color="error" max={99} invisible={unreadCount === 0}>
          <NotificationsNoneIcon fontSize="medium" />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              width: 400,
              maxHeight: 480,
              mt: 1,
              borderRadius: 2,
              boxShadow: 4,
              overflow: 'hidden',
            },
          },
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            bgcolor: 'grey.50',
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="subtitle1" fontWeight={700}>
            Notifications
          </Typography>
          {unreadCount > 0 && (
            <Typography variant="caption" color="primary.main" fontWeight={600}>
              {unreadCount} unread
            </Typography>
          )}
        </Box>

        {loading && sortedNotifications.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : sortedNotifications.length === 0 ? (
          <Box sx={{ px: 2, py: 4, textAlign: 'center' }}>
            <NotificationsNoneIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              No notifications
            </Typography>
          </Box>
        ) : (
          <List disablePadding sx={{ overflowY: 'auto', maxHeight: 400 }}>
            {sortedNotifications.map((notification, index) => (
              <Box key={notification.id}>
                <NotificationItem notification={notification} onSelect={handleSelect} />
                {index < sortedNotifications.length - 1 && <Divider />}
              </Box>
            ))}
          </List>
        )}
      </Menu>
    </>
  );
}
